/**
 * Which company am I working in?
 *
 * This is not a settings toggle buried two menus deep. It sits at the top of the
 * document screen, always visible, because every number on the page below it
 * belongs to this company: the invoices listed, the letterhead, the GSTIN, the
 * bank account, the numbering series. A user who cannot see which company they
 * are in can print an invoice under the wrong GSTIN, and that is not a
 * recoverable mistake.
 *
 * Shown only when there is more than one company to choose between. With a single
 * company there is nothing to switch to, and a control that does nothing is worse
 * than no control.
 */
import { Building2, ChevronDown, Check } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useActiveBusiness, useBusinesses, useSwitchBusiness } from './useDocuments';
import { toast } from '@ui/components/Toaster';

export function BusinessSwitcher() {
  const businesses = useBusinesses();
  const activeBusiness = useActiveBusiness();
  const switchTo = useSwitchBusiness();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement | null>(null);

  const list = businesses.data ?? [];
  // The ACTIVE one, read from the stored preference -- not from
  // `switchTo.variables`, which is merely the last thing this component asked
  // for and is undefined until the user switches for the first time. Conflating
  // the two would show "Choose a company" on every fresh load.
  const active = list.find((b) => b.id === activeBusiness.data);

  // Escape and outside-click both close it.
  //
  // A menu that only closes on its own button is a menu a user can get stuck
  // behind, and "I clicked the building icon and nothing happened" is exactly
  // what an open-but-unreachable menu looks like from the outside. The listener
  // is `pointerdown` rather than `click` so the tap that opens the menu does not
  // immediately close it again.
  //
  // THIS HOOK MUST STAY ABOVE THE `return null` BELOW. It was below it, and that
  // is a React hooks violation, not a style point: while the businesses query is
  // still loading the component returned early and never called the hook, then
  // on the next render it did — and React throws "Rendered more hooks than
  // during the previous render", which crashed the whole app. The symptom was
  // an import screen that silently stopped working, four thousand lines away
  // from the cause.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onDown = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
    };
  }, [open]);

  // One company is not a choice. Rendering a disabled dropdown would be noise.
  // Every hook above runs unconditionally, so this may safely bail.
  if (list.length < 2) return null;

  function choose(id: string, name: string) {
    setOpen(false);
    if (id === active?.id) return;
    switchTo.mutate(id, {
      onError: (error: unknown) => {
        toast({
          title: 'Could not switch company',
          description: error instanceof Error ? error.message : String(error),
          tone: 'danger',
        });
      },
      onSuccess: () => {
        toast({ title: `Now working in ${name}`, tone: 'success' });
      },
    });
  }

  return (
    // `relative` + an ABSOLUTE menu.
    //
    // The menu used to be a plain `<ul>` sitting as a flex SIBLING of the button
    // inside `flex items-center gap-2`, inside a 256px sidebar that also holds a
    // GSTIN label. Flex squeezed it: the button and the menu competed for the same
    // row, and with two long company names the menu collapsed to nothing — the
    // click toggled `open` correctly and the user saw no change at all, which is
    // the most confusing possible failure of a control whose whole job is to say
    // which company you are in.
    //
    // Absolute positioning takes it out of the flow entirely, so it cannot be
    // squeezed by a sibling, and `z-50` puts it above the nav list it now
    // overlays. `min-w` stops a long GSTIN from narrowing it to nothing.
    <div className="relative z-40 flex flex-col gap-1" ref={root}>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          data-testid="business-switcher"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-haspopup="true"
          className="flex items-center gap-2 rounded border border-slate-700 bg-slate-900/70 px-3 py-1.5 text-sm text-slate-200 hover:border-slate-500"
        >
          <Building2 className="h-4 w-4" aria-hidden />
          <span className="font-medium">{active?.legalName ?? 'Choose a company'}</span>
          <ChevronDown className="h-4 w-4" aria-hidden />
        </button>

        {active?.gstin && <span className="text-xs text-slate-500">GSTIN {active.gstin}</span>}
      </div>

      {open && (
        // NO `role` overrides here, deliberately.
        //
        // These are `<button>` elements and they must STAY buttons. Adding
        // `role="listbox"` / `role="option"` is a11y-correct in the abstract and
        // wrong here: an explicit role replaces the native one, so every
        // `getByRole('button', ...)` — including the three Playwright tests that
        // use this control to prove company switching works — stopped matching,
        // and the menu was unreachable by assistive tech looking for a button
        // too. A `listbox` also demands keyboard arrow navigation this component
        // does not implement, so the label promised behaviour the code lacks.
        <ul
          data-testid="business-switcher-list"
          className="absolute left-0 top-full z-50 mt-1 min-w-56 rounded border border-slate-700 bg-slate-900 p-1 shadow-lg"
        >
          {list.map((b) => (
            <li key={b.id}>
              <button
                type="button"
                onClick={() => choose(b.id, b.legalName)}
                className="flex w-full items-center justify-between gap-4 rounded px-3 py-2 text-left text-sm text-slate-200 hover:bg-slate-800"
              >
                <span>{b.legalName}</span>
                {b.id === active?.id && <Check className="h-4 w-4" aria-hidden />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
